import { AssetsService } from '@inference.engine/assets-api-client/web';
import { queryOptions } from '@tanstack/react-query';
import { assetsHttp } from '@/lib/api/assets-client';
import { isAssetId } from '@/shared/lib/asset-id';
import type { AssetUrlEntryMap, AssetUrlMap, AssetVariant } from '../model/types';
import { ASSET_QUERY_KEYS } from './query-keys';

/** The service caps a batch at 500 ids, so larger sets are chunked. */
const BATCH_LIMIT = 500;

const toUrlMap = (entries: AssetUrlEntryMap): AssetUrlMap =>
  Object.fromEntries(Object.entries(entries).map(([assetId, entry]) => [assetId, entry.url]));

/**
 * Resolved URL entries for a set of stored asset ids, optionally for a derived
 * `variant` rather than the original.
 *
 * Services that own content store only the asset id, so anything rendering that
 * content resolves the URLs itself. They are signed and expire, hence the short
 * `staleTime` — long enough to avoid refetching while a page is read, short
 * enough that a URL never goes stale in place.
 *
 * Ids that fail to resolve are simply absent from the result, matching the
 * service's own contract; callers treat absence as "no artwork yet".
 */
export const assetUrlEntriesQueryOptions = (assetIds: readonly string[], variant?: AssetVariant) =>
  queryOptions({
    queryKey: ASSET_QUERY_KEYS.urls(assetIds, variant),
    enabled: assetIds.length > 0,
    staleTime: 5 * 60 * 1000,
    queryFn: async ({ signal }): Promise<AssetUrlEntryMap> => {
      // Only ids the service will accept. It validates every entry and refuses the whole batch on
      // one malformed value, so a single stray reference would otherwise take every picture with
      // it. A filtered-out id is simply absent from the answer, which callers already read as
      // unresolved - so it is reported rather than hidden.
      const unique = [...new Set(assetIds.filter(isAssetId))];
      if (unique.length === 0) return {};
      const batches: string[][] = [];
      for (let index = 0; index < unique.length; index += BATCH_LIMIT) {
        batches.push(unique.slice(index, index + BATCH_LIMIT));
      }

      const responses = await Promise.all(
        batches.map((batch) =>
          AssetsService.getAssetUrls({
            client: assetsHttp,
            body: { assetIds: batch, variant },
            signal,
          }),
        ),
      );

      return Object.fromEntries(
        responses
          .flatMap((response) => response.data?.urls ?? [])
          .map(({ assetId, url, width, height }) => [assetId, { url, width, height }]),
      );
    },
  });

/**
 * The same resolved entries, flattened to the `{ id: url }` map the app has
 * always used. Existing callers that don't need width/height keep working
 * unchanged; only a caller that passes a `variant` sees anything different.
 */
export const assetUrlsQueryOptions = (assetIds: readonly string[], variant?: AssetVariant) => ({
  ...assetUrlEntriesQueryOptions(assetIds, variant),
  select: toUrlMap,
});
